/**
 * useClienteImportModals - Hook for ClientesPage
 *
 * Manages the three import modal flows:
 * - Hospitais import (cliente-hospital Excel)
 * - Representantes import (representante Excel)
 * - Clientes bulk import (clientes Excel)
 */
import { useState } from 'react';
import type { ImportResult, RepresentanteImportResult, ClientesImportResult } from './useClienteExcel';

// --- Hospitais import state ---

export interface HospitaisImportModalState {
  show: boolean;
  file: File | null;
  result: ImportResult | null;
}

// --- Representantes import state ---

export interface RepresentantesImportModalState {
  show: boolean;
  file: File | null;
  result: RepresentanteImportResult | null;
}

// --- Clientes import state ---

export interface ClientesImportModalState {
  show: boolean;
  file: File | null;
  result: ClientesImportResult | null;
}

export interface UseClienteImportModalsConfig {
  importHospitaisFromExcel: (file: File) => Promise<ImportResult>;
  importRepresentantesFromExcel: (file: File) => Promise<RepresentanteImportResult>;
  importClientesFromExcel: (file: File) => Promise<ClientesImportResult>;
  onHospitaisImported: () => void;
  onRepresentantesImported: () => void;
  onClientesImported: () => void;
}

export interface UseClienteImportModalsReturn {
  // Hospitais
  hospitaisModal: HospitaisImportModalState;
  showFormatInfoModal: boolean;
  setShowImportHospitaisModal: (v: boolean) => void;
  setShowFormatInfoModal: (v: boolean) => void;
  setImportFile: (f: File | null) => void;
  handleImportHospitais: () => Promise<void>;
  handleCloseImportModal: () => void;

  // Representantes
  representantesModal: RepresentantesImportModalState;
  showRepresentanteFormatInfoModal: boolean;
  setShowImportRepresentantesModal: (v: boolean) => void;
  setShowRepresentanteFormatInfoModal: (v: boolean) => void;
  setImportRepresentanteFile: (f: File | null) => void;
  handleImportRepresentantes: () => Promise<void>;
  handleCloseImportRepresentantesModal: () => void;

  // Clientes
  clientesModal: ClientesImportModalState;
  showClienteFormatInfoModal: boolean;
  setShowImportClientesModal: (v: boolean) => void;
  setShowClienteFormatInfoModal: (v: boolean) => void;
  setImportClienteFile: (f: File | null) => void;
  handleImportClientes: () => Promise<void>;
  handleCloseImportClientesModal: () => void;
}

export const useClienteImportModals = ({
  importHospitaisFromExcel,
  importRepresentantesFromExcel,
  importClientesFromExcel,
  onHospitaisImported,
  onRepresentantesImported,
  onClientesImported,
}: UseClienteImportModalsConfig): UseClienteImportModalsReturn => {
  // Hospitais
  const [showImportHospitaisModal, setShowImportHospitaisModal] = useState(false);
  const [showFormatInfoModal, setShowFormatInfoModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Representantes
  const [showImportRepresentantesModal, setShowImportRepresentantesModal] = useState(false);
  const [showRepresentanteFormatInfoModal, setShowRepresentanteFormatInfoModal] = useState(false);
  const [importRepresentanteFile, setImportRepresentanteFile] = useState<File | null>(null);
  const [importRepresentanteResult, setImportRepresentanteResult] = useState<RepresentanteImportResult | null>(null);

  // Clientes
  const [showImportClientesModal, setShowImportClientesModal] = useState(false);
  const [showClienteFormatInfoModal, setShowClienteFormatInfoModal] = useState(false);
  const [importClienteFile, setImportClienteFile] = useState<File | null>(null);
  const [importClienteResult, setImportClienteResult] = useState<ClientesImportResult | null>(null);

  const handleImportHospitais = async () => {
    if (!importFile) return;
    const result = await importHospitaisFromExcel(importFile);
    setImportResult(result);
    if (result.success || result.created > 0 || result.updated > 0) {
      onHospitaisImported();
    }
  };

  const handleCloseImportModal = () => {
    setShowImportHospitaisModal(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleImportRepresentantes = async () => {
    if (!importRepresentanteFile) return;
    const result = await importRepresentantesFromExcel(importRepresentanteFile);
    setImportRepresentanteResult(result);
    if (result.success || result.representantes_created > 0 || result.representantes_updated > 0) {
      onRepresentantesImported();
    }
  };

  const handleCloseImportRepresentantesModal = () => {
    setShowImportRepresentantesModal(false);
    setImportRepresentanteFile(null);
    setImportRepresentanteResult(null);
  };

  const handleImportClientes = async () => {
    if (!importClienteFile) return;
    const result = await importClientesFromExcel(importClienteFile);
    setImportClienteResult(result);
    if (result.success || result.criados > 0 || result.atualizados > 0) {
      onClientesImported();
    }
  };

  const handleCloseImportClientesModal = () => {
    setShowImportClientesModal(false);
    setImportClienteFile(null);
    setImportClienteResult(null);
  };

  return {
    // Hospitais
    hospitaisModal: {
      show: showImportHospitaisModal,
      file: importFile,
      result: importResult,
    },
    showFormatInfoModal,
    setShowImportHospitaisModal,
    setShowFormatInfoModal,
    setImportFile,
    handleImportHospitais,
    handleCloseImportModal,

    // Representantes
    representantesModal: {
      show: showImportRepresentantesModal,
      file: importRepresentanteFile,
      result: importRepresentanteResult,
    },
    showRepresentanteFormatInfoModal,
    setShowImportRepresentantesModal,
    setShowRepresentanteFormatInfoModal,
    setImportRepresentanteFile,
    handleImportRepresentantes,
    handleCloseImportRepresentantesModal,

    // Clientes
    clientesModal: {
      show: showImportClientesModal,
      file: importClienteFile,
      result: importClienteResult,
    },
    showClienteFormatInfoModal,
    setShowImportClientesModal,
    setShowClienteFormatInfoModal,
    setImportClienteFile,
    handleImportClientes,
    handleCloseImportClientesModal,
  };
};
